import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowRight, X, AlertTriangle, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { trackDonateClick, getGa4ClientId } from "../lib/analytics";
import { beginExternalNav, finishExternalNav, isFramed } from "../lib/externalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

// Kept in sync with backend ALL_PACKAGES. Backend is the source of truth for
// the actual amount charged — this table is UX only.
const CAMPAIGN_PACKAGES = [
  { id: "campaign_25",  label: "$25" },
  { id: "campaign_50",  label: "$50" },
  { id: "campaign_100", label: "$100" },
  { id: "campaign_250", label: "$250" },
];
const GENERAL_PACKAGES = [
  { id: "general_50",  label: "$50" },
  { id: "general_100", label: "$100" },
  { id: "general_250", label: "$250" },
  { id: "general_500", label: "$500" },
];

/**
 * LiveDonateDialog — real Stripe Checkout flow.
 *
 * Same props shape as DonateComingSoonDialog so any callsite that renders
 * one can render the other unchanged. The wrapper in DonateComingSoonDialog
 * picks between them based on REACT_APP_DONATIONS_LIVE.
 *
 * Props:
 *   open, onOpenChange
 *   campaignId?     — undefined → General Fund
 *   studentName?    — display context only (personalises the headline)
 */
// "Marcus Thompson" → "Marcus T." for the roll's default display name.
const shortName = (full) => {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

export const LiveDonateDialog = ({ open, onOpenChange, campaignId, studentName }) => {
  const isCampaign = Boolean(campaignId);
  const packages = isCampaign ? CAMPAIGN_PACKAGES : GENERAL_PACKAGES;

  const [selectedId, setSelectedId] = useState(packages[1]?.id || packages[0]?.id);
  const [custom, setCustom] = useState("");
  const [customFocused, setCustomFocused] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [rollOptIn, setRollOptIn] = useState(false);
  const [rollName, setRollName] = useState("");
  const [rollCity, setRollCity] = useState("");
  const [wallOpen, setWallOpen] = useState(false);
  const [wallAnonymous, setWallAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Founding window: while open, every gift joins the Founding Donors wall.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    client.get("/founding-donors/window")
      .then((r) => { if (alive) setWallOpen(Boolean(r.data?.open)); })
      .catch(() => { if (alive) setWallOpen(false); });
    return () => { alive = false; };
  }, [open]);

  // Reset state each open — no leaking selections between different campaigns.
  // Also force recurring=false whenever we're in campaign mode (30-day
  // student campaigns don't support perpetual monthly gifts — the toggle
  // itself is hidden below, this is a defensive reset).
  useEffect(() => {
    if (open) {
      setSelectedId(packages[1]?.id || packages[0]?.id);
      setCustom("");
      setCustomFocused(false);
      setRecurring(false);
      setRollOptIn(false);
      setRollName("");
      setRollCity("");
      setWallAnonymous(false);
      setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  const customNum = useMemo(() => {
    const v = parseFloat(custom);
    return Number.isFinite(v) ? v : null;
  }, [custom]);
  const customValid = customNum !== null && customNum >= 1 && customNum <= 10000;
  const usingCustom = customFocused || (custom && custom.length > 0);

  const submit = async () => {
    setErr("");
    setSubmitting(true);
    let win = null;
    try {
      const payload = {
        origin_url: window.location.origin,
        campaign_id: campaignId || undefined,
        donor_name: donorName.trim() || undefined,
        // Attach GA4 client_id so the server-side Measurement Protocol
        // event fires against the same visitor when the webhook lands.
        ga_client_id: getGa4ClientId() || undefined,
      };
      if (wallOpen && wallAnonymous) payload.wall_anonymous = true;
      if (usingCustom) {
        if (!customValid) {
          setErr("Please enter an amount between $1 and $10,000.");
          setSubmitting(false);
          return;
        }
        payload.amount_cents = Math.round(customNum * 100);
      } else {
        payload.package_id = selectedId;
      }
      if (recurring) {
        payload.recurring = true;
        if (rollOptIn) {
          payload.roll_opt_in = true;
          payload.roll_display_name = rollName.trim() || undefined;
          payload.roll_city = rollCity.trim() || undefined;
        }
      }
      // Fire the top-of-funnel conversion BEFORE the redirect — GA4
      // won't have a chance to capture it once we leave to Stripe.
      trackDonateClick({
        campaignId,
        amountUsd: (payload.amount_cents || 0) / 100 || undefined,
        isRecurring: !!payload.recurring,
      });
      win = beginExternalNav();
      const r = await client.post("/donations/checkout", payload);
      finishExternalNav(win, r.data.checkout_url);   // Stripe Checkout (new tab when embedded)
      if (isFramed()) setSubmitting(false);
    } catch (e) {
      if (win && !win.closed) win.close();
      setErr(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="donate-dialog"
        className="max-w-lg border-border bg-sand p-0 gap-0 sm:rounded-lg overflow-hidden"
      >
        <div className="relative overflow-hidden bg-beige px-8 py-6">
          <button
            type="button"
            aria-label="Close"
            data-testid="donate-close"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-full bg-sand/70 p-1.5 text-ink/70 hover:bg-sand hover:text-ink"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>

          <DialogTitle className="relative font-serif text-3xl font-normal leading-[1.05] text-ink">
            {recurring
              ? "Become a sustaining donor."
              : (isCampaign && studentName ? `Give with ${studentName} in mind.` : "Give to the General Fund")}
          </DialogTitle>
          <DialogDescription className="relative mt-2 text-[13px] leading-relaxed text-olive">
            {recurring
              ? "Monthly gifts keep the General Fund ready year-round — so we can help the next verified student the moment they need it. Pause or cancel anytime. 100% tax-deductible."
              : isCampaign
                ? `Your gift is made to My Rich Aunt, a 501(c)(3), and helps cover approved student expenses. Choosing ${studentName || "this student"} is a non-binding preference; My Rich Aunt awards every grant. 100% tax-deductible.`
                : "Your gift funds the pool that pays for verified students' most urgent needs. 100% tax-deductible."}
          </DialogDescription>
        </div>

        <div className="px-8 py-6 space-y-5">
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive mb-2">
              Choose an amount
            </p>
            <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Donation amount">
              {packages.map((p) => {
                const active = !usingCustom && selectedId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`donate-preset-${p.id}`}
                    onClick={() => { setSelectedId(p.id); setCustom(""); setCustomFocused(false); }}
                    className={`rounded-xl border py-3 text-center font-serif text-3xl transition-all ${ active ? "border-ink bg-ink text-sand shadow-sm" : "border-border bg-bone text-ink hover:border-ink" }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <label className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-bone px-4 py-3 text-[13px] focus-within:border-ink">
              <span className="font-serif text-3xl text-olive">$</span>
              <input
                type="number"
                inputMode="decimal"
                min="1"
                max="10000"
                step="1"
                placeholder="Other amount"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                onFocus={() => setCustomFocused(true)}
                onBlur={() => { if (!custom) setCustomFocused(false); }}
                data-testid="donate-custom-amount"
                className="flex-1 bg-transparent font-serif text-3xl text-ink placeholder:text-olive/60 focus:outline-none"
              />
              {custom && !customValid && (
                <span className="text-[13px] text-scarlet">$1 – $10,000</span>
              )}
            </label>
          </div>

          <div>
            <label htmlFor="donate-donor-name" className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
              Your name <span className="text-olive/60 normal-case tracking-normal">(optional)</span>
            </label>
            <input
              id="donate-donor-name"
              type="text"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              placeholder="How should we thank you?"
              maxLength={120}
              data-testid="donate-donor-name"
              className="mt-1.5 w-full rounded-xl border border-border bg-bone px-4 py-2.5 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
            />
          </div>

          {/* Monthly-recurring toggle — hidden for browse-student campaigns.
              Student campaigns are live for 30 days (with a possible 15-day
              extension), so monthly recurring only makes sense for the
              General Fund (sustaining donors). */}
          {!isCampaign && (
          <label
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-[13px] cursor-pointer transition-colors ${ recurring ? "border-ink bg-beige" : "border-border bg-bone hover:border-ink/50" }`}
            data-testid="donate-recurring-toggle"
          >
            <span
              className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                recurring ? "bg-ink" : "bg-olive/30"
              }`}
            >
              <span
                className={`h-4 w-4 rounded-full bg-sand shadow-sm transition-transform ${
                  recurring ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={recurring}
              onChange={(e) => setRecurring(e.target.checked)}
            />
            <Repeat className="h-4 w-4 text-olive" strokeWidth={2} />
            <span className="flex-1">
              <span className="font-semibold text-ink">Become a sustaining donor</span>
              <span className="block text-[13px] text-olive mt-0.5">
                {recurring
                  ? "You'll give this amount every month until you cancel — keeping the General Fund ready year-round."
                  : "Monthly gifts sustain the fund between campaigns, so we can help the next student the day they ask."}
              </span>
            </span>
          </label>
          )}

          {/* Sustaining donor roll — names only, off by default, monthly only. */}
          {!isCampaign && recurring && (
          <div className="space-y-3 rounded-xl border border-border bg-bone px-4 py-3" data-testid="donate-roll-block">
            <label className="flex cursor-pointer items-start gap-3 text-[13px]">
              <Checkbox
                checked={rollOptIn}
                onCheckedChange={(v) => {
                  const on = v === true;
                  setRollOptIn(on);
                  if (on && !rollName) setRollName(shortName(donorName));
                }}
                data-testid="donate-roll-opt-in"
                className="mt-0.5 h-5 w-5 rounded border-ink/40 data-[state=checked]:bg-ink data-[state=checked]:text-sand"
              />
              <span className="flex-1">
                <span className="font-semibold text-ink">List me on the sustaining donor roll</span>
                <span className="mt-0.5 block text-[13px] text-olive">
                  A quiet thank-you on the homepage. Names only, never amounts. Removed automatically if you cancel.
                </span>
              </span>
            </label>
            {rollOptIn && (
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={rollName}
                  onChange={(e) => setRollName(e.target.value)}
                  placeholder='Name to show, e.g. "Marcus T."'
                  maxLength={60}
                  data-testid="donate-roll-name"
                  className="w-full rounded-xl border border-border bg-sand px-4 py-2.5 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
                />
                <input
                  type="text"
                  value={rollCity}
                  onChange={(e) => setRollCity(e.target.value)}
                  placeholder="City (optional)"
                  maxLength={60}
                  data-testid="donate-roll-city"
                  className="w-full rounded-xl border border-border bg-sand px-4 py-2.5 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
                />
              </div>
            )}
          </div>
          )}

          {/* Founding window — every beta gift lands on the Founding Donors wall. */}
          {wallOpen && (
          <div className="rounded-xl border border-yellow/70 bg-yellow/[0.22] px-4 py-3" data-testid="donate-founding-note">
            <p className="text-[13px] font-semibold text-ink">
              Beta gift: your name joins the Founding Donors wall.
            </p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-olive">
              Every gift during our beta launch is a founding gift. We&rsquo;ll list you on the{" "}
              <a href="/founding" target="_blank" rel="noreferrer" className="underline decoration-ink/30 underline-offset-2 hover:text-ink" data-testid="donate-founding-wall-link">
                Founding Donors page
              </a>{" "}
              and email you a private link to add a photo, alma mater or tribute.
            </p>
            <label className="mt-2.5 flex cursor-pointer items-start gap-3 text-[13px]">
              <Checkbox
                checked={wallAnonymous}
                onCheckedChange={(v) => setWallAnonymous(v === true)}
                data-testid="donate-wall-anonymous"
                className="mt-0.5 h-5 w-5 rounded border-ink/40 bg-bone data-[state=checked]:bg-ink data-[state=checked]:text-sand"
              />
              <span className="text-ink">Keep my name off the wall (list me as Anonymous)</span>
            </label>
          </div>
          )}

          {err && (
            <p role="alert" data-testid="donate-error"
               className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} /> {err}
            </p>
          )}

          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={submit}
            disabled={submitting || (usingCustom && !customValid)}
            data-testid="donate-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Taking you to Stripe…</>
            ) : recurring ? (
              <>Become a sustaining donor <ArrowRight className="h-4 w-4" strokeWidth={2} /></>
            ) : (
              <>Continue securely <ArrowRight className="h-4 w-4" strokeWidth={2} /></>
            )}
          </motion.button>

          <p className="flex items-center justify-center gap-1.5 text-[13px] text-olive">
            <Lock className="h-3 w-3" strokeWidth={2} />
            You&rsquo;ll enter payment on Stripe&rsquo;s secure page.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
